import { minBy } from 'lodash';
import { BreakPointsAndValues } from './types';

// defines granurality of fluid sizes
// (we are not using viewport units)
const targetSizes = [0, 375, 576, 767, 950, 1024, 1200, 1300, 1400] as const;

export function bouValueLg(value: number) {
  return bouValue(value, 1920);
}

export function bouValue(value: number, bouDesignWidth: number) {
  const closestTarget = [...targetSizes].reverse().find(size => {
    return size <= bouDesignWidth;
  });

  if (closestTarget === undefined) {
    throw Error('Unexpectedly no closest target size found');
  }

  const breakPoints = enrich([{ width: bouDesignWidth, value }]);
  const result = getSizeForTarget(closestTarget, breakPoints);
  return `${result}px`;
}

/**
 * @deprecated since 2.10.2023
 * For spacing, reference the customTheme's space key (src/ui/views/root/custom-theme.ts).
 */
export function fluidSpaceLg(cssProperty: string, sizeLg: number, important?: boolean) {
  const breakPoints = getBreakPoints(sizeLg);
  return fluid(cssProperty, breakPoints, false, important);
}

function getBreakPoints(sizeLg: number) {
  return [
    {
      width: 1920,
      value: sizeLg * 1.0,
    },
    {
      width: 1400,
      value: sizeLg * 0.72,
    },
    {
      width: 1200,
      value: sizeLg * 0.65,
    },
    {
      width: 1024,
      value: sizeLg * 0.53,
    },
    {
      width: 768,
      value: sizeLg * 0.49,
    },
    {
      width: 375,
      value: sizeLg * 0.42,
    },
  ];
}

// dirty trick
export function enrich(sizes: BreakPointsAndValues) {
  const size1200 = sizes.find(size => size.width === 1200);
  const size768 = sizes.find(size => size.width === 768);
  const size1920 = sizes.find(size => size.width === 1920);
  if (!size1200 && size768 && size768.value) {
    const value = size768.value * 1.08;
    const generated1200 = {
      width: 1200,
      value,
    };
    return [...sizes, generated1200];
  }
  if (!size1200 && size1920 && size1920.value) {
    const value = size1920.value * 0.65;
    const generated1200 = {
      width: 1200,
      value,
    };
    return [...sizes, generated1200];
  }
  return sizes;
}

/**
 * @deprecated since 2.10.2023
 * For breakpoints, reference the customTheme's breakpoints key (src/ui/views/root/custom-theme.ts).
 * In Primer components, you can style responsively by providing an array to the sx prop.
 */
export function fluid(
  cssProperty: string,
  _breakPointsAndSizes: BreakPointsAndValues,
  isPercentage: boolean = false,
  important?: boolean,
) {
  const breakPointsAndSizes = enrich(_breakPointsAndSizes);

  const unit = isPercentage ? '%' : 'px';
  const sizes = targetSizes.map(width => {
    const size = getSizeForTarget(width, breakPointsAndSizes);
    // OA: using !important smells, but I added it to avoid tampering with css resets for <ul>
    return `
      @media(min-width: ${width}px) {
        ${cssProperty}: ${size}${unit}${important ? '!important' : ''};
      } `;
  });
  return sizes.join('');
}

function getSizeForTarget(width: number, breakPointsAndSizes: BreakPointsAndValues) {
  function getClosestLarger(w: number) {
    return minBy(
      breakPointsAndSizes.filter(d => d.width >= w),
      d => d.width - w,
    );
  }

  function getClosestSmaller(w: number) {
    return minBy(
      breakPointsAndSizes.filter(d => w >= d.width),
      d => w - d.width,
    );
  }

  const larger = getClosestLarger(width);
  const smaller = getClosestSmaller(width);

  if (!larger && smaller) {
    return smaller.value;
  }
  if (!smaller && larger) {
    return larger.value;
  }

  if (!larger || !smaller) {
    throw Error(`Unexpectedly simultanenously no smaller AND larger size found for ${width}`);
  }

  if (smaller.width === width) {
    return smaller.value;
  }
  if (larger.width === width) {
    return larger.value;
  }
  const slope = (larger.value - smaller.value) / (larger.width - smaller.width);
  const widthDelta = width - smaller.width;
  return smaller.value + slope * widthDelta;
}
